import type { CompRef } from '@wix/document-services-types';
import {
  AbstractVirtualManifestBuilder,
  IntersectionVirtualManifestBuilder,
  IVirtualBuilder,
  ManifestBuilderStrategy,
  MergeVirtualManifestBuilder,
} from '../../builders';

import { ManifestStore } from '../../stores';
import { ManifestServiceFlags } from '../../ManifestServiceFlags';
import { ManifestServiceStats } from '../../ManifestServiceStats';
import { hash } from '../../utils';
import {
  createEditorPlatformManifestServicesError,
  EditorPlatformManifestServicesErrorCode,
} from '../../errors';
import { DependenciesContributor } from '../../dependencies';
import type {
  EditorPointer,
  EditorPointerServiceAPI,
} from '@wix/editor-document-logic-api';

export interface IVirtualManifestBuilderProps {
  editorPointers?: EditorPointer<CompRef>[] | EditorPointer<CompRef>;
  strategy?: ManifestBuilderStrategy;
  contextBuilder?: (
    builder: AbstractVirtualManifestBuilder,
  ) => IVirtualBuilder | void;
  cacheNamespace?: string;
}

export class VirtualManifestBuilder {
  private static getEditorPointerCacheKey(
    editorPointer: EditorPointer<CompRef>,
  ) {
    const variants = editorPointer.documentPointer?.variants;
    const variantContext = variants?.length
      ? `|variants:${variants
          .map((variant) => variant.id)
          .sort()
          .join(',')}`
      : '';

    return `${editorPointer.asUniqueKey}${variantContext}`;
  }

  static generateVirtualManifestId(
    editorPointers: EditorPointer<CompRef>[],
    strategy: ManifestBuilderStrategy = ManifestBuilderStrategy.Intersection,
    namespace: string | undefined = undefined,
    source: string | undefined = undefined,
  ) {
    const tags = [
      `s:${strategy}`,
      `ns:${namespace ?? 'generic'}`,
      `src:${source ?? 'unknown'}`,
    ].join('|');

    return `${editorPointers
      .map((x) => this.getEditorPointerCacheKey(x))
      .join('_')}___${tags}`;
  }

  static generateHashVirtualManifestId() {
    return `hash:${hash()}`;
  }

  static isHashGenerateVirtualManifestId(virtualManifestId: string) {
    return virtualManifestId.startsWith('hash:');
  }

  constructor(
    private readonly dependenciesContributor: DependenciesContributor,
    private readonly manifestStore: ManifestStore,
    private readonly editorPointerAPI: EditorPointerServiceAPI,
    private readonly manifestServiceFlags: ManifestServiceFlags,
    private readonly manifestServiceStats: ManifestServiceStats,
  ) {}

  #getVirtualManifestBuilder(
    strategy: ManifestBuilderStrategy,
  ): AbstractVirtualManifestBuilder {
    switch (strategy) {
      case ManifestBuilderStrategy.Intersection:
        return new IntersectionVirtualManifestBuilder(
          this.manifestStore,
          this.dependenciesContributor,
          this.editorPointerAPI,
          this.manifestServiceFlags,
        );

      case ManifestBuilderStrategy.Merge:
        return new MergeVirtualManifestBuilder(
          this.manifestStore,
          this.dependenciesContributor,
          this.editorPointerAPI,
          this.manifestServiceFlags,
        );
    }

    throw createEditorPlatformManifestServicesError(
      EditorPlatformManifestServicesErrorCode.ManifestContextBuild,
      'unknown builder strategy',
    ).withMessage(strategy);
  }

  buildVirtualManifest(props: IVirtualManifestBuilderProps) {
    const start = performance.now();
    const strategy = props.strategy ?? ManifestBuilderStrategy.Intersection;
    const editorPointers = props.editorPointers as EditorPointer<CompRef>[];

    let builder = this.#getVirtualManifestBuilder(strategy);

    if (props.contextBuilder) {
      const customBuilder = props.contextBuilder(
        builder,
      ) as AbstractVirtualManifestBuilder; // AbstractVirtualManifestBuilder vs IVirtualBuilder type issue

      if (customBuilder) {
        builder = customBuilder;
      }
    }

    if (editorPointers?.length) {
      editorPointers.forEach((editorPointer) => {
        builder.withEditorPointer(editorPointer);
      });
    }

    const virtualManifest = builder.build('RAW_BUILD');

    this.manifestServiceStats.method(
      'buildVirtualManifest',
      false,
      performance.now() - start,
    );

    return virtualManifest;
  }
}
